import React, { useEffect, useState } from 'react';

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Start fade out after 2.5 seconds
    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, 2500);

    // Complete splash after 3 seconds
    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, 3000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(135deg, #FFF5F5 0%, #FFEEF2 50%, #FFF0F3 100%)',
      }}
    >
      {/* Animated glow rings */}
      <div className="relative flex items-center justify-center">
        {/* Outer glow ring 1 */}
        <div
          className="absolute rounded-full animate-pulse-glow-1"
          style={{
            width: '280px',
            height: '280px',
            background: 'radial-gradient(circle, rgba(233, 30, 99, 0.15) 0%, transparent 70%)',
          }}
        />
        
        {/* Outer glow ring 2 */}
        <div
          className="absolute rounded-full animate-pulse-glow-2"
          style={{
            width: '240px',
            height: '240px',
            background: 'radial-gradient(circle, rgba(233, 30, 99, 0.2) 0%, transparent 70%)',
          }}
        />
        
        {/* Outer glow ring 3 */}
        <div
          className="absolute rounded-full animate-pulse-glow-3"
          style={{
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(233, 30, 99, 0.25) 0%, transparent 70%)',
          }}
        />

        {/* Rotating light effect */}
        <div
          className="absolute rounded-full animate-rotate-glow"
          style={{
            width: '180px',
            height: '180px',
            background: 'conic-gradient(from 0deg, transparent 0%, rgba(233, 30, 99, 0.3) 25%, transparent 50%, rgba(233, 30, 99, 0.3) 75%, transparent 100%)',
            filter: 'blur(10px)',
          }}
        />

        {/* Heart container with shadow */}
        <div
          className="relative z-10 animate-heartbeat"
          style={{
            filter: 'drop-shadow(0 10px 40px rgba(233, 30, 99, 0.4))',
          }}
        >
          {/* Red circle background */}
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: '120px',
              height: '120px',
              background: 'linear-gradient(145deg, #E91E63 0%, #C2185B 100%)',
              boxShadow: '0 8px 32px rgba(233, 30, 99, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.2)',
            }}
          >
            {/* White heart */}
            <svg
              width="60"
              height="55"
              viewBox="0 0 24 22"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* App name */}
      <div className="absolute bottom-20 text-center">
        <h1
          className="text-2xl font-bold tracking-wide animate-fade-in-up"
          style={{ color: '#E91E63' }}
        >
          qbool.ca
        </h1>
        <p
          className="mt-2 text-sm text-gray-500 animate-fade-in-up-delay"
        >
          Find Your Perfect Match
        </p>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes pulse-glow-1 {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.3;
          }
        }

        @keyframes pulse-glow-2 {
          0%, 100% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.4;
          }
        }

        @keyframes pulse-glow-3 {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.5;
          }
        }

        @keyframes rotate-glow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes heartbeat {
          0%, 100% {
            transform: scale(1);
          }
          15% {
            transform: scale(1.1);
          }
          30% {
            transform: scale(1);
          }
          45% {
            transform: scale(1.08);
          }
          60% {
            transform: scale(1);
          }
        }

        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-pulse-glow-1 {
          animation: pulse-glow-1 2s ease-in-out infinite;
        }

        .animate-pulse-glow-2 {
          animation: pulse-glow-2 2s ease-in-out infinite;
          animation-delay: 0.3s;
        }

        .animate-pulse-glow-3 {
          animation: pulse-glow-3 2s ease-in-out infinite;
          animation-delay: 0.6s;
        }

        .animate-rotate-glow {
          animation: rotate-glow 3s linear infinite;
        }

        .animate-heartbeat {
          animation: heartbeat 1.5s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          animation-delay: 0.5s;
          opacity: 0;
        }

        .animate-fade-in-up-delay {
          animation: fade-in-up 0.8s ease-out forwards;
          animation-delay: 0.8s;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
